import { NavigateFunction } from "react-router-dom";
import { NavigationItem } from "./navigation-index";

// Recent searches storage
const RECENT_SEARCHES_KEY = 'superclinic-recent-searches';
const FREQUENT_DESTINATIONS_KEY = 'superclinic-frequent-destinations';
const MAX_RECENT_SEARCHES = 20;
const MAX_FREQUENT_DESTINATIONS = 10;

export class NavigationService {
  private navigate: NavigateFunction;
  
  constructor(navigate: NavigateFunction) {
    this.navigate = navigate;
  }
  
  // Navigate to a navigation item
  navigateToItem(item: NavigationItem, clientId?: string): void {
    let path = item.path;
    
    // Replace client ID placeholder if needed
    if (item.metadata?.clientRequired && clientId) {
      path = path.replace(':clientId', clientId);
    } else if (item.metadata?.clientRequired && !clientId) {
      // If client is required but not provided, navigate to client selection
      console.warn('Client ID required for navigation but not provided');
      return;
    }
    
    // Track usage
    this.trackUsage(item.id);
    
    // Navigate
    this.navigate(path);
  }
  
  // Track usage for frequently accessed items
  trackUsage(itemId: string): void {
    const stored = localStorage.getItem(FREQUENT_DESTINATIONS_KEY);
    const frequentDestinations: Record<string, number> = stored ? JSON.parse(stored) : {};
    
    frequentDestinations[itemId] = (frequentDestinations[itemId] || 0) + 1;
    
    // Keep only top destinations
    const sorted = Object.entries(frequentDestinations)
      .sort(([, a], [, b]) => b - a)
      .slice(0, MAX_FREQUENT_DESTINATIONS);
    
    const trimmed = Object.fromEntries(sorted);
    localStorage.setItem(FREQUENT_DESTINATIONS_KEY, JSON.stringify(trimmed));
  }
  
  // Add to recent searches
  addRecentSearch(query: string): void {
    if (!query.trim()) return;
    
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    const recentSearches: string[] = stored ? JSON.parse(stored) : [];
    
    // Remove if already exists and add to front
    const filtered = recentSearches.filter(s => s !== query);
    const updated = [query, ...filtered].slice(0, MAX_RECENT_SEARCHES);
    
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  }
  
  // Get recent searches
  getRecentSearches(): string[] {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  }
  
  // Get frequent destinations
  getFrequentDestinations(): string[] {
    const stored = localStorage.getItem(FREQUENT_DESTINATIONS_KEY);
    if (!stored) return [];
    
    const frequentDestinations: Record<string, number> = JSON.parse(stored);
    return Object.keys(frequentDestinations);
  }
  
  // Clear user data (on logout)
  clearUserData(): void {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
    localStorage.removeItem(FREQUENT_DESTINATIONS_KEY);
  }
}

// Helper to determine if a path requires a client ID
export function requiresClientId(path: string): boolean {
  return path.includes(':clientId');
}

// Helper to extract tab parameter from path
export function extractTabFromPath(path: string): string | null {
  const match = path.match(/[?&]tab=([^&]+)/);
  return match ? match[1] : null;
}

// Helper to build path with query parameters
export function buildPathWithParams(basePath: string, params: Record<string, string>): string {
  const queryString = Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
  
  return queryString ? `${basePath}?${queryString}` : basePath;
}